import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGuardarHoras, useHoras, useNomina } from '@/hooks/useManoObra';
import { useObrasEstados } from '@/hooks/useObras';
import { useSemanas } from '@/hooks/useReportes';
import { formatearCRC } from '@adelante/shared';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

export function HorasObraPantalla() {
  const semanasQ = useSemanas();
  const obrasQ = useObrasEstados();
  const horasQ = useHoras();
  const nominaQ = useNomina();
  const guardar = useGuardarHoras();
  const fileRef = useRef<HTMLInputElement>(null);

  const semanas = semanasQ.data?.data ?? [];
  const obras = obrasQ.data?.data ?? [];
  const horas = horasQ.data?.data ?? [];
  const nominas = nominaQ.data?.data ?? [];

  const [semana, setSemana] = useState('');
  // obra_codigo -> horas (string para el input).
  const [valores, setValores] = useState<Record<string, string>>({});
  // Semana cuyas horas ya precargamos. Evita que un refetch de `horas` (p.ej.
  // al volver el foco a la ventana tras elegir el Excel) PISE lo que el usuario
  // importó/escribió pero aún no guardó — bug que hacía guardar 0 obras.
  const semanaCargadaRef = useRef<string | null>(null);

  // Al CAMBIAR de semana, precargar UNA sola vez las horas ya guardadas.
  useEffect(() => {
    if (!semana || horasQ.isLoading) return;
    if (semanaCargadaRef.current === semana) return;
    semanaCargadaRef.current = semana;
    const sem = Number(semana);
    const ex: Record<string, string> = {};
    for (const h of horas) {
      if (Number(h.semana_operativa_id) === sem) ex[h.obra_codigo] = String(h.horas);
    }
    setValores(ex);
  }, [semana, horas, horasQ.isLoading]);

  const totalHoras = useMemo(
    () => Object.values(valores).reduce((a, v) => a + (Number(v) || 0), 0),
    [valores],
  );
  const montoSemana = useMemo(() => {
    const n = nominas.find((x) => Number(x.semana_operativa_id) === Number(semana));
    return n ? Number(n.monto_nomina_directa) : 0;
  }, [nominas, semana]);

  // Filas a mostrar = obras habilitadas + cualquier obra importada que NO esté
  // en esa lista (para que nada de lo importado quede invisible en la tabla).
  const filasRender = useMemo(() => {
    const codes = obras.map((o) => o.codigo);
    const set = new Set(codes);
    const extra = Object.keys(valores).filter((c) => !set.has(c) && (Number(valores[c]) || 0) > 0);
    return [...codes, ...extra];
  }, [obras, valores]);

  function setHoras(obra: string, v: string) {
    setValores((prev) => ({ ...prev, [obra]: v }));
  }

  function descargarPlantilla() {
    const aoa = [['obra', 'horas'], ...obras.map((o) => [o.codigo, ''])];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Horas');
    XLSX.writeFile(wb, 'plantilla_horas.xlsx');
  }

  function importar(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]!];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws ?? {});
        const next: Record<string, string> = {};
        let n = 0;
        for (const r of rows) {
          const obra = String(r.obra ?? r.Obra ?? r.OBRA ?? '').trim();
          const hrs = Number(r.horas ?? r.Horas ?? r.HORAS ?? 0);
          if (obra && hrs > 0) {
            next[obra] = String(hrs);
            n++;
          }
        }
        setValores((prev) => ({ ...prev, ...next }));
        toast.success(`Importadas ${n} obras con horas`, {
          description: 'Revisá la tabla y presioná «Guardar» para persistir los cambios.',
        });
      } catch (err) {
        toast.error('No se pudo leer el Excel', {
          description: err instanceof Error ? err.message : 'Formato inválido',
        });
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  }

  async function handleGuardar() {
    if (!semana) return;
    const filas = Object.entries(valores)
      .map(([obra_codigo, v]) => ({ obra_codigo, horas: Number(v) || 0 }))
      .filter((f) => f.horas > 0);
    try {
      await guardar.mutateAsync({ semana_operativa_id: Number(semana), filas });
      toast.success(`Horas guardadas (${filas.length} obras)`);
    } catch (e) {
      toast.error('No se pudo guardar', { description: e instanceof Error ? e.message : 'Error' });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Horas por Obra</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Las horas determinan cómo se reparte la nómina proporcional a cada obra.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={descargarPlantilla}>
            Descargar plantilla
          </Button>
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!semana}>
            Importar Excel
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={importar}
          />
          <Button onClick={handleGuardar} disabled={!semana || guardar.isPending}>
            Guardar
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div>
          <Label className="mb-1 block text-xs">Semana operativa</Label>
          <Select value={semana} onValueChange={setSemana}>
            <SelectTrigger className="w-[240px]">
              <SelectValue placeholder="Seleccionar semana…" />
            </SelectTrigger>
            <SelectContent>
              {semanas.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {s.anio} · Semana {s.numero_semana}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {semana && (
          <p className="text-xs text-muted-foreground">
            Total:{' '}
            <strong>{totalHoras.toLocaleString('en-US', { maximumFractionDigits: 1 })} hrs</strong>
            {montoSemana > 0 && <> · Nómina: {formatearCRC(montoSemana)}</>}
            {montoSemana === 0 && ' · ⚠ sin nómina registrada para esta semana'}
          </p>
        )}
      </div>

      {!semana ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Elegí una semana para cargar/editar las horas.
        </p>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Obra</TableHead>
                <TableHead className="text-right">Horas</TableHead>
                <TableHead className="text-right">% del total</TableHead>
                <TableHead className="text-right">Nómina asignada</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filasRender.map((codigo) => {
                const v = valores[codigo] ?? '';
                const hrs = Number(v) || 0;
                const pct = totalHoras > 0 ? (hrs / totalHoras) * 100 : 0;
                const asignada = totalHoras > 0 ? (hrs / totalHoras) * montoSemana : 0;
                return (
                  <TableRow key={codigo}>
                    <TableCell className="font-mono text-xs font-semibold">{codigo}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        value={v}
                        onChange={(e) => setHoras(codigo, e.target.value)}
                        className="ml-auto h-8 w-24 text-right"
                        placeholder="0"
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-muted-foreground">
                      {pct > 0 ? `${pct.toFixed(1)}%` : '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {asignada > 0 ? formatearCRC(asignada) : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
